import React, { useEffect, useState } from 'react';

import { useHistory } from 'react-router';
import { t } from 'i18next';
import { DateTime } from 'luxon';

import { AssignmentFragment, Chats } from '@school_ify/schoolify-repository/src/shared/types';
import {
  TeacherAssignmentTypeEnum,
  useGetParentSubjectForTeachingStructureIdQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import { useEvent } from 'hooks/useEvent';
import Task from 'views/Study/components/Forms/Task';
import { useAppDispatch } from 'redux/hooks';
import {
  getAllStudents,
  selectedStudentsBaseSelection,
  resetStudentsList,
} from 'redux/liveClass/students.slices';
import ModalDialog from 'components/ModalDialog/ModalDialog';
import { IModel } from 'utils/IStudy';
import SelectClass from 'clean/components/SelectClassAndSubject/SelectClass';
import SelectSubject from 'clean/components/SelectClassAndSubject/SelectSubject';

import './EditTaskModal.scoped.scss';

interface Props {
  onClose: () => void;
  open: boolean;
  sendData: (assignments: any) => void;
  content?: AssignmentFragment;
}

const AddTask: React.FC<Props> = ({ onClose, open, content }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const saveEvent = useEvent();
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState({
    title: t('common:SelectSubject'), // 'Select subject',
    id: null,
  });
  const [model, setModel] = useState<IModel>({
    id: null,
    teaching_structure_id: '',
    title: '',
    description: '',
    duration: 45,
    due_at: DateTime.now().toISO(),
    type: TeacherAssignmentTypeEnum.TASK,
    chat_id: null,
    assignment_grade_weight_id: null,
  });

  const { data: parentSubjects } = useGetParentSubjectForTeachingStructureIdQuery({
    variables: {
      leaf_id: content?.teaching_structure?.id || '',
    },
    fetchPolicy: 'network-only',
    skip: !content?.teaching_structure?.id,
  });

  useEffect(() => {
    if (parentSubjects?.teaching_structure_parents?.length) {
      const parentSubject = parentSubjects?.teaching_structure_parents[0];
      setSelectedSubject({
        title: parentSubject.title,
        id: parentSubject.id,
      });
    }
  }, [parentSubjects]);

  useEffect(() => {
    if (content) {
      const editModel: IModel = {
        id: content.id,
        teaching_structure_id: content?.teaching_structure?.id,
        title: content.title,
        description: content.description,
        duration: content.duration,
        due_at: content.due_at,
        // @ts-ignore
        type: content.assignment_type,
        chat_id: content.chat?.id,
        assignment_grade_weight_id: content.grade_weight?.id || null,
      };
      setModel(editModel);
      dispatch(
        selectedStudentsBaseSelection(
          content.assignment_users.map(assignmentUser => assignmentUser.user_id)
        )
      );
    } else {
      dispatch(selectedStudentsBaseSelection([]));
    }
  }, [content]);

  useEffect(() => {
    if (!selectedClass) {
      return;
    }
    dispatch(getAllStudents(selectedClass.id));
  }, [selectedClass]);

  useEffect(() => {
    dispatch(resetStudentsList());
  }, []);

  const handleSave = () => {
    saveEvent.trigger();
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title={content ? t('common:edit_task') : t('common:add_task')}
      actions={[
        {
          type: 'filled',
          colour: 'primary',
          label: t('common:save'),
          handler: handleSave,
        },
        {
          type: 'decline',
          colour: 'primary',
          label: t('common:cancel'),
          handler: onClose,
        },
      ]}
    >
      <div className="add-task-popover">
        <div className="flex gap-1">
          <div className="flex flex-col mb-1 w-full">
            <SelectClass handleSelect={setSelectedClass} selectedId={selectedClass?.id} />
          </div>
          <div className="flex flex-col mb-1 w-full">
            <SelectSubject
              handleSelect={setSelectedSubject}
              parentId={selectedClass?.id}
              selectedId={selectedSubject.id}
            />
          </div>
        </div>
        <Task
          actionsCentered
          subject={selectedSubject.id}
          data={model}
          assignmentFiles={content?.files}
          chat={content?.chat as Chats}
          gradeWeight={content?.grade_weight}
          title={''}
          saveEvent={saveEvent}
          renderWithoutActions
          onSave={() => {
            history.push(`/?update=${Math.random()}`);
            onClose();
          }}
        />
      </div>
    </ModalDialog>
  );
};

export default AddTask;
